import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAppealCalendar } from '../services/suiteApi';

const POLL_INTERVAL_MS = 60 * 1000;
const HORIZON_DAYS = 7;

/**
 * Topbar bell that polls upcoming appeal-filing deadlines and shows a count
 * badge. Dropdown lists the next deadlines with a link to the case run page.
 *
 * Failures are swallowed -- the bell shows zero rather than ever surfacing an
 * error in the chrome.
 */
export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const resp = await fetchAppealCalendar({ horizonDays: HORIZON_DAYS });
      const list = Array.isArray(resp?.data)
        ? resp.data
        : (resp?.data?.items || []);
      setItems(list);
    } catch (_) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('click', onDoc);
    return () => window.removeEventListener('click', onDoc);
  }, [open]);

  const count = items.length;
  const badgeLabel = count > 9 ? '9+' : String(count);

  return (
    <div className="dm-bell" ref={wrapRef}>
      <button
        type="button"
        className="dm-bell__btn"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={`${count} upcoming filing deadline${count === 1 ? '' : 's'}`}
        title={`${count} upcoming filing deadline${count === 1 ? '' : 's'}`}
      >
        <span className="dm-bell__icon" aria-hidden="true">!</span>
        {count > 0 && <span className="dm-bell__badge">{badgeLabel}</span>}
      </button>
      {open && (
        <div className="dm-bell__menu">
          <div className="dm-bell__header">
            Filing deadlines · next {HORIZON_DAYS} days
          </div>
          {count === 0 ? (
            <div className="dm-bell__empty">No deadlines coming up. Nice.</div>
          ) : (
            <ul className="dm-bell__list">
              {items.slice(0, 8).map((d, i) => {
                const due = d.dueDate || d.DueDate || d.due_date || '';
                const caseId = d.caseId || d.CaseId || d.case_id;
                const payer = d.payerName || d.PayerName || d.payer_name || '';
                const label = d.label || d.appealLevel || d.AppealLevel
                  || 'Appeal deadline';
                const href = caseId ? `/denials/${caseId}` : '/AppealBuilder';
                return (
                  <li key={d.deadlineId || d.DeadlineId || `${caseId}-${i}`}
                        className="dm-bell__item">
                    <Link to={href} onClick={() => setOpen(false)}
                            className="dm-bell__link">
                      <div className="dm-bell__when">{due || 'soon'}</div>
                      <div className="dm-bell__what">{label}</div>
                      {payer && <div className="dm-bell__who">{payer}</div>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="dm-bell__footer">
            <Link to="/AppealBuilder" onClick={() => setOpen(false)}>
              View calendar
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
